import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SetPasswordPage() {
  const { language, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [verifying, setVerifying] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null); // {valid, email, reason}
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenInfo({ valid: false, reason: 'no_token' });
      setVerifying(false);
      return;
    }
    fetch(`${API_URL}/api/auth/verify-setup-token?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then(setTokenInfo)
      .catch(() => setTokenInfo({ valid: false, reason: 'network' }))
      .finally(() => setVerifying(false));
  }, [token]);

  const sk = (s, c) => (language === 'sk' ? s : c);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(sk('Heslo musí mať aspoň 6 znakov', 'Heslo musí mít alespoň 6 znaků'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(sk('Heslá sa nezhodujú', 'Hesla se neshodují'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to set password');
      }
      toast.success(sk('Heslo nastavené, prihlasujem vás…', 'Heslo nastaveno, přihlašuji vás…'));
      if (data.user && setUser) {
        setUser(data.user);
      }
      setTimeout(() => navigate('/member'), 1200);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center p-4" data-testid="set-password-page">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
          <p className="text-sm text-muted-foreground mt-3">
            {sk('Overujem odkaz…', 'Ověřuji odkaz…')}
          </p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenInfo?.valid) {
    const reasonText = {
      no_token: sk('Odkaz neobsahuje token. Skontrolujte URL z emailu.', 'Odkaz neobsahuje token. Zkontrolujte URL z e-mailu.'),
      token_not_found: sk('Tento odkaz nebol nájdený. Vyžiadajte si nový.', 'Tento odkaz nebyl nalezen. Vyžádejte si nový.'),
      already_used: sk('Tento odkaz už bol použitý. Pre obnovu hesla použite "Zabudli ste heslo?".', 'Tento odkaz již byl použit. Pro obnovu hesla použijte "Zapomněli jste heslo?".'),
      expired: sk('Platnosť odkazu vypršala. Vyžiadajte si nový cez "Zabudli ste heslo?".', 'Platnost odkazu vypršela. Vyžádejte si nový přes "Zapomněli jste heslo?".'),
      network: sk('Sieťová chyba pri overovaní odkazu. Skúste znova.', 'Síťová chyba při ověřování odkazu. Zkuste znovu.'),
    }[tokenInfo?.reason] || sk('Neplatný odkaz.', 'Neplatný odkaz.');
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-hover border border-border/40 p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-destructive" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-text-heading mb-3">
            {sk('Odkaz nie je platný', 'Odkaz není platný')}
          </h1>
          <p className="text-muted-foreground text-sm mb-6">{reasonText}</p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-primary hover:bg-primary-hover text-white rounded-full px-8"
            data-testid="set-password-back-login-btn"
          >
            {sk('Späť na prihlásenie', 'Zpět na přihlášení')}
          </Button>
        </div>
      </div>
    );
  }

  // Valid: show password form
  return (
    <div className="min-h-screen bg-cream-subtle flex items-center justify-center p-4" data-testid="set-password-form">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-hover border border-border/40 p-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-text-heading mb-2">
            {sk('Nastavte si vaše heslo', 'Nastavte si své heslo')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {tokenInfo.email && (
              <>
                {sk('pre účet ', 'pro účet ')}
                <span className="font-medium text-foreground">{tokenInfo.email}</span>
              </>
            )}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              {sk('Nové heslo', 'Nové heslo')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={sk('aspoň 6 znakov', 'alespoň 6 znaků')}
                className="pl-10 pr-10 rounded-xl"
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              {sk('Potvrďte heslo', 'Potvrďte heslo')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type={showPwd ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={sk('zopakujte heslo', 'zopakujte heslo')}
                className="pl-10 rounded-xl"
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting || !password || !confirmPassword}
            className="w-full bg-primary hover:bg-primary-hover text-white rounded-xl py-6 mt-2"
            data-testid="set-password-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {sk('Ukladám…', 'Ukládám…')}
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                {sk('Nastaviť heslo a prihlásiť', 'Nastavit heslo a přihlásit')}
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
